/**
 * Вид обложки курса — одно место на весь вики-фронт (владелец 16.10.2026).
 *
 * Ключ wiki_courses.cover_key бывает трёх форматов (бэкенд проверяет в wiki-cover.ts):
 *   - `sushi-set`, `bowl`, … — брендовая иллюстрация из public/brand/illustrations;
 *   - `emoji:<смайлик>`     — смайлик на плитке (шрифт Noto Color Emoji — 'FK Emoji');
 *   - `icon:<uuid>`         — своя иконка из общей библиотеки (/api/wiki/cover-icons/:id/file).
 * Плюс cover_color (фон `#RRGGBB`) и cover_text (надпись на плитке).
 *
 * Ничего не выбрано → курс НЕ получает одну и ту же креветку: картинка и пастельный фон
 * берутся по хэшу id курса — у каждого курса свой вид, пока кто-то не выберет обложку.
 * Чистый модуль без React — покрыт тестом.
 */

/** Брендовые иллюстрации (файлы public/brand/illustrations/*.png). */
export const BRAND_COVER_KEYS = [
  'sushi-set',
  'gunkan-ikura',
  'bowl',
  'nigiri',
  'onigiri-mono',
  'maki-character',
  'maki-character-filled',
  'sushi-doodles-mono',
] as const;

/** Подложки брендовых картинок при явном выборе (как в прототипе PageLearn). */
const BRAND_BG: Record<string, string> = {
  'sushi-set': '#EAF6FF',
  'gunkan-ikura': '#FDE8EC',
  bowl: '#FFF1E6',
  nigiri: '#FFF1E6',
  'onigiri-mono': '#EEF0F7',
  'maki-character': '#FFF1E6',
  'maki-character-filled': '#FFF1E6',
  'sushi-doodles-mono': '#EEF0F7',
};

/** Тёмные градиенты шапки курса для брендовых картинок (белый текст поверх). */
const BRAND_HERO: Record<string, string> = {
  'sushi-set': 'linear-gradient(135deg, #1F3B63, #0F6FC0)',
  'gunkan-ikura': 'linear-gradient(135deg, #5A1F33, #B23A55)',
  bowl: 'linear-gradient(135deg, #5A2E0E, #D9580E)',
  nigiri: 'linear-gradient(135deg, #5A2E0E, #D9580E)',
  'onigiri-mono': 'linear-gradient(135deg, #2A2342, #4B3F7A)',
  'maki-character': 'linear-gradient(135deg, #5A2E0E, #D9580E)',
  'maki-character-filled': 'linear-gradient(135deg, #5A2E0E, #D9580E)',
  'sushi-doodles-mono': 'linear-gradient(135deg, #2A2342, #4B3F7A)',
};

/** Пастельные фоны для выбора цвета и для автоподбора (мягкие, в тон бренду, держат тёмный текст). */
export const COVER_PASTELS = [
  '#EAF6FF', // небесный
  '#FDE8EC', // розовый
  '#FFF1E6', // персик
  '#FFE3CC', // светлый апельсин
  '#FFF4C9', // сливочный
  '#E3F6E8', // мятный
  '#DFF3F0', // морская волна
  '#E8E4FB', // сиреневый
  '#FBE3F2', // роза
  '#E6F0FF', // барвинок
  '#F3EBDD', // песок
  '#EEF0F7', // лаванда-серый
] as const;

/** Цвет текста на светлой плитке (бренд #251D4A) и на тёмной. */
const INK_DARK = '#251D4A';
const INK_LIGHT = '#FFFFFF';
const NEUTRAL_BG = '#EEF0F7';
const NEUTRAL_HERO = 'linear-gradient(135deg, #2A2342, #4B3F7A)';

export const COVER_ICON_URL = (id: string): string => `/api/wiki/cover-icons/${id}/file`;
export const brandCoverSrc = (key: string): string => `/brand/illustrations/${key}.png`;

const HEX_RE = /^#[0-9a-f]{6}$/i;
const ICON_RE = /^icon:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** Что нужно знать об обложке: поля курса из списка/карточки (id — для автоподбора). */
export interface CoverSource {
  id?: string | null;
  coverKey?: string | null;
  coverColor?: string | null;
  coverText?: string | null;
}

export interface CoverVisual {
  /** brand — иллюстрация; emoji — смайлик; icon — своя иконка; none — нечего показывать (курса ещё нет). */
  kind: 'brand' | 'emoji' | 'icon' | 'none';
  /** Адрес картинки (brand/icon). */
  src?: string;
  emoji?: string;
  /** Фон плитки. */
  bg: string;
  /** Тёмный градиент шапки (белый текст поверх). */
  heroBg: string;
  /** Подпись на плитке и цвет её текста (тёмный на светлом фоне, белый на тёмном). */
  text?: string;
  textColor: string;
  /** Обложка подобрана автоматически — владелец ничего не выбирал. */
  auto: boolean;
  /** Что показать, если иконка из библиотеки не загрузилась (удалена): картинка по хэшу id. */
  fallbackSrc?: string;
}

/** Стабильный 32-битный хэш строки (djb2): один и тот же id всегда даёт один и тот же вид. */
export function hashStr(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h >>> 0;
}

/** Второй, независимый от первого, хэш — чтобы картинка и цвет не были связаны. */
function hash2(h: number): number {
  return Math.imul(h ^ (h >>> 15), 2246822519) >>> 0;
}

// ── Цвет ────────────────────────────────────────────────────────────

export function hexToRgb(hex: string): [number, number, number] | null {
  if (!HEX_RE.test(hex)) return null;
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

const toHex = (n: number): string => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

/** Относительная яркость по WCAG (0 — чёрный, 1 — белый). */
export function luminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 1;
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Контраст двух цветов (1…21). */
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function hexToHsl(hex: string): [number, number, number] {
  const rgb = hexToRgb(hex) ?? [255, 255, 255];
  const [r, g, b] = rgb.map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, sat, l];
}

function hslToHex(h: number, sat: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return `#${toHex((r + m) * 255)}${toHex((g + m) * 255)}${toHex((b + m) * 255)}`;
}

/** Цвет текста поверх фона: тёмный на светлом, белый на тёмном — тот, у которого контраст выше. */
export function textOn(bg: string): string {
  return contrast(bg, INK_DARK) >= contrast(bg, INK_LIGHT) ? INK_DARK : INK_LIGHT;
}

/**
 * Тёмный градиент шапки из цвета обложки: «темнее → базовый». Светлый пастельный цвет
 * затемняем по яркости (оттенок сохраняем, насыщенность не ниже 35 %) ровно настолько,
 * чтобы белый текст читался (контраст ≥ 4.5); тёмный цвет остаётся как есть.
 */
export function heroFromColor(color: string): string {
  const [h, s0, l0] = hexToHsl(color);
  const sat = Math.max(s0, 0.35);
  let l = l0;
  while (l > 0.12 && contrast(hslToHex(h, sat, l), INK_LIGHT) < 4.5) l -= 0.02;
  const end = l === l0 ? color : hslToHex(h, sat, l);
  return `linear-gradient(135deg, ${hslToHex(h, sat, Math.max(0.07, l - 0.12))}, ${end})`;
}

// ── Основное ────────────────────────────────────────────────────────

/** Разбор ключа: что это за обложка (null — пусто или ключ неизвестен). */
export function parseCoverKey(key: string | null | undefined):
  | { kind: 'brand'; key: string }
  | { kind: 'emoji'; emoji: string }
  | { kind: 'icon'; id: string }
  | null {
  if (!key) return null;
  if ((BRAND_COVER_KEYS as readonly string[]).includes(key)) return { kind: 'brand', key };
  if (key.startsWith('emoji:') && key.length > 'emoji:'.length) return { kind: 'emoji', emoji: key.slice('emoji:'.length) };
  const m = ICON_RE.exec(key);
  if (m) return { kind: 'icon', id: m[1].toLowerCase() };
  return null;
}

/** Ключ обложки для смайлика / иконки — единственное место, где собирается строка ключа. */
export const emojiCoverKey = (emoji: string): string => `emoji:${emoji}`;
export const iconCoverKey = (id: string): string => `icon:${id}`;

/**
 * Вид обложки. Принимает поля курса целиком (`coverVisual(course)`), либо только ключ
 * (`coverVisual(course.coverKey)`) — тогда автоподбор по id недоступен.
 */
export function coverVisual(source: CoverSource | string | null | undefined): CoverVisual {
  const s: CoverSource = typeof source === 'string' || source == null ? { coverKey: source ?? null } : source;
  const parsed = parseCoverKey(s.coverKey);
  const color = s.coverColor && HEX_RE.test(s.coverColor) ? s.coverColor.toUpperCase() : null;
  const text = s.coverText?.trim() || undefined;
  // Семя автоподбора: id курса; без id — сам ключ (иконка/смайлик красятся по себе), иначе «нет».
  const seed = s.id || (parsed && parsed.kind !== 'brand' ? s.coverKey! : '');
  const h = seed ? hashStr(seed) : 0;
  const autoBrand = BRAND_COVER_KEYS[hash2(h) % BRAND_COVER_KEYS.length];
  const autoPastel = COVER_PASTELS[h % COVER_PASTELS.length];

  const done = (v: Omit<CoverVisual, 'textColor' | 'text'>): CoverVisual => ({ ...v, text, textColor: textOn(v.bg) });

  if (parsed?.kind === 'brand') {
    const bg = color ?? BRAND_BG[parsed.key];
    return done({ kind: 'brand', src: brandCoverSrc(parsed.key), bg, heroBg: color ? heroFromColor(color) : BRAND_HERO[parsed.key], auto: false });
  }
  if (parsed?.kind === 'emoji') {
    const bg = color ?? autoPastel;
    return done({ kind: 'emoji', emoji: parsed.emoji, bg, heroBg: heroFromColor(bg), auto: false });
  }
  if (parsed?.kind === 'icon') {
    const bg = color ?? autoPastel;
    return done({ kind: 'icon', src: COVER_ICON_URL(parsed.id), bg, heroBg: heroFromColor(bg), auto: false, fallbackSrc: brandCoverSrc(autoBrand) });
  }
  // Ничего не выбрано. Есть id — у каждого курса свой вид; нет id (курс ещё не создан) — «пусто».
  if (!seed) {
    const bg = color ?? NEUTRAL_BG;
    return done({ kind: 'none', bg, heroBg: color ? heroFromColor(color) : NEUTRAL_HERO, auto: true });
  }
  const bg = color ?? autoPastel;
  return done({ kind: 'brand', src: brandCoverSrc(autoBrand), bg, heroBg: heroFromColor(bg), auto: true });
}
